/**
 * Analytics consent store + loaders for GA4 and Microsoft Clarity.
 *
 * #1216 / RESEARCH/BUDGIT_WEBSITE_PRIVACY_BASELINE_2026_08_06.md (F1, F4)
 *
 * - Non-essential analytics must not load before an explicit choice.
 * - Choice persists in localStorage; banner reappears only until decided.
 * - GA4 uses Consent Mode defaults (denied) then grant on accept.
 * - Clarity is gated on the same choice and must not run on authenticated
 *   financial screens until masking is verified (owner Clarity dashboard).
 */

import { isAnalyticsExcludedPath } from '../utils/sensitiveLinkToken';

export const ANALYTICS_CONSENT_STORAGE_KEY = 'budgit_analytics_consent';
export const ANALYTICS_CONSENT_VERSION = 1;

/** @typedef {'granted' | 'denied'} AnalyticsConsentChoice */

const listeners = new Set();

function safeStorage() {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null;
    return window.localStorage;
  } catch (_) {
    return null;
  }
}

/**
 * @returns {{ choice: AnalyticsConsentChoice, version: number, decidedAt: number } | null}
 */
export function readAnalyticsConsentRecord() {
  const storage = safeStorage();
  if (!storage) return null;
  try {
    const raw = storage.getItem(ANALYTICS_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || (parsed.choice !== 'granted' && parsed.choice !== 'denied')) {
      return null;
    }
    return {
      choice: parsed.choice,
      version: typeof parsed.version === 'number' ? parsed.version : 1,
      decidedAt: typeof parsed.decidedAt === 'number' ? parsed.decidedAt : 0,
    };
  } catch (_) {
    return null;
  }
}

/** @returns {AnalyticsConsentChoice | null} */
export function getAnalyticsConsent() {
  const record = readAnalyticsConsentRecord();
  return record ? record.choice : null;
}

export function hasAnalyticsConsentDecision() {
  return getAnalyticsConsent() != null;
}

/**
 * @param {AnalyticsConsentChoice} choice
 */
export function setAnalyticsConsent(choice) {
  if (choice !== 'granted' && choice !== 'denied') {
    throw new Error('analytics consent must be granted or denied');
  }
  const storage = safeStorage();
  const record = {
    choice,
    version: ANALYTICS_CONSENT_VERSION,
    decidedAt: Date.now(),
  };
  if (storage) {
    try {
      storage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, JSON.stringify(record));
    } catch (_) {
      // Private mode / quota — still apply in-memory for this session.
    }
  }
  listeners.forEach((fn) => {
    try {
      fn(choice);
    } catch (_) {}
  });
  return record;
}

/**
 * Drop the stored choice and tell listeners so the banner can open again.
 * `null` means no decision. It is not a grant or a denial.
 */
export function clearAnalyticsConsent() {
  const storage = safeStorage();
  if (storage) {
    try {
      storage.removeItem(ANALYTICS_CONSENT_STORAGE_KEY);
    } catch (_) {
      // Private mode / quota. Listeners still reopen the banner.
    }
  }
  listeners.forEach((fn) => {
    try {
      fn(null);
    } catch (_) {}
  });
}

/**
 * Subscribe to consent changes (banner, loaders).
 * `null` means the stored choice was cleared.
 * @param {(choice: AnalyticsConsentChoice | null) => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribeAnalyticsConsent(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// ---------------------------------------------------------------------------
// Google Analytics 4 — single load path, consent-gated
// ---------------------------------------------------------------------------

let gaLoadStarted = false;

function ensureGtagStub() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

/**
 * Install Consent Mode defaults (denied) before any tags load.
 * Safe to call on every page boot; no network request.
 */
export function installGtagConsentDefaults() {
  if (typeof window === 'undefined') return;
  ensureGtagStub();
  // Consent Mode v2 defaults — no storage until grant.
  window.gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'denied',
    personalization_storage: 'denied',
    security_storage: 'granted',
    wait_for_update: 500,
  });
}

function applyGtagConsentUpdate(choice) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  if (choice === 'granted') {
    window.gtag('consent', 'update', {
      analytics_storage: 'granted',
    });
  } else {
    window.gtag('consent', 'update', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
    });
  }
}

/**
 * Load gtag.js + config only when consent is granted.
 * Idempotent. Measurement ID from REACT_APP_GA_MEASUREMENT_ID (no hardcode).
 */
export function enableGoogleAnalytics() {
  if (typeof window === 'undefined') return false;
  if (getAnalyticsConsent() !== 'granted') return false;
  // Token-link pages (email preferences) never load analytics.
  if (isAnalyticsExcludedPath(window.location.pathname)) return false;

  const measurementId = process.env.REACT_APP_GA_MEASUREMENT_ID;
  if (!measurementId) {
    if (process.env.NODE_ENV === 'development') {
      console.debug('[Analytics] REACT_APP_GA_MEASUREMENT_ID not set — skipping GA');
    }
    return false;
  }

  ensureGtagStub();
  applyGtagConsentUpdate('granted');

  if (gaLoadStarted) {
    // Already loaded — re-config is harmless for SPA navigations.
    window.gtag('config', measurementId);
    return true;
  }

  try {
    const existing = document.querySelector(
      `script[src*="googletagmanager.com/gtag/js"][src*="${measurementId}"]`
    );
    if (!existing) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      document.head.appendChild(script);
    }
    gaLoadStarted = true;
    window.gtag('js', new Date());
    window.gtag('config', measurementId);
    return true;
  } catch (error) {
    console.error('[Analytics] Failed to initialize Google Analytics 4:', error);
    return false;
  }
}

/**
 * Call when user denies — keeps Consent Mode denied; does not load gtag network.
 */
export function denyGoogleAnalytics() {
  ensureGtagStub();
  applyGtagConsentUpdate('denied');
}

/**
 * Footer "Cookie choices" control. Clears the stored grant or denial and
 * returns Consent Mode to denied until the banner is answered again.
 */
export function reopenCookieChoices() {
  clearAnalyticsConsent();
  denyGoogleAnalytics();
}

/**
 * Boot-time GA policy: defaults only, enable if previously granted.
 */
export function bootGoogleAnalyticsFromConsent() {
  installGtagConsentDefaults();
  if (getAnalyticsConsent() === 'granted') {
    enableGoogleAnalytics();
  }
}

// ---------------------------------------------------------------------------
// Clarity helpers used by clarityService / AuthContext
// ---------------------------------------------------------------------------

/**
 * Whether Clarity may initialize for this session.
 * Requires analytics consent granted. Authenticated financial app recording
 * is blocked until masking is verified (F1 residual).
 *
 * @param {{ allowAuthenticated?: boolean }} [opts]
 */
export function mayInitializeClarity(opts = {}) {
  if (getAnalyticsConsent() !== 'granted') return false;
  if (typeof window !== 'undefined' && isAnalyticsExcludedPath(window.location.pathname)) return false;
  // Default: never record signed-in sessions until data-clarity-mask is real.
  if (opts.allowAuthenticated === true) return true;
  // Callers that pass isAuthenticated must set allowAuthenticated only when
  // masking is confirmed. Public marketing with consent is fine.
  if (opts.isAuthenticated) return false;
  return true;
}
